import React, { useMemo, useState } from "react";

/**
 * Column picker, driven entirely by the field catalogue from the server.
 *
 * Nothing here hardcodes a field list. The list view, the card view, the filter
 * bar and the CSV export all read the same catalogue, which is what stops them
 * drifting apart the way four separate hardcoded lists did.
 *
 * Fields are grouped by origin first, because the distinction matters when
 * reading a report: what a provider published is a record, what the model read
 * off the creative is an interpretation.
 */

// Shared with the filter builder, so a field sits under the same heading and
// colour whether it is being shown or filtered on.
export const ORIGIN_ORDER = ["api", "ai", "derived", "internal"];

// AI is yellow everywhere it appears, so a model's interpretation is never
// mistaken for something a provider published. The other three stay muted.
export const ORIGIN_META = {
  api:      { label: "From the platform", short: "Platform", hint: "Published by the Ads Network Provider", color: "#0e7490" },
  ai:       { label: "Read from the image", short: "AI", hint: "Inferred by the vision model", color: "#a16207" },
  derived:  { label: "Calculated by us", short: "Calculated", hint: "Computed from provider data", color: "#7c3aed" },
  internal: { label: "Our records", short: "Our records", hint: "Brand mapping, lifecycle, capture state", color: "#4b5563" },
};

/**
 * A small tag saying where a value comes from.
 *
 * Deliberately quiet for the three factual origins and loud only for AI, whose
 * values are an interpretation rather than a record.
 */
export function OriginTag({ origin, style }) {
  const meta = ORIGIN_META[origin];
  if (!meta) return null;
  const ai = origin === "ai";
  return (
    <span
      title={`${meta.label}. ${meta.hint}.`}
      style={{
        display: "inline-block", verticalAlign: "middle", marginLeft: 6,
        fontSize: 9, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase",
        lineHeight: 1.4, padding: "0 5px", borderRadius: 4, whiteSpace: "nowrap",
        color: ai ? "#713f12" : "#a3acb9",
        background: ai ? "#fef08a" : "transparent",
        border: `1px solid ${ai ? "#facc15" : "#e5e7eb"}`,
        ...style,
      }}
    >
      {meta.short}
    </span>
  );
}

export default function FieldPicker({
  catalog = [],
  selected = [],
  onChange,
  view = "list",
  max = 15,
  defaults = [],
  onClose,
}) {
  const [query, setQuery] = useState("");
  const chosen = useMemo(() => new Set(selected), [selected]);

  // Every field for this provider, in both views. The picker used to offer
  // only fields hand-tagged for the view (26 for cards), so most of what we
  // hold could not be shown on a card at all. `views` now only seeds defaults.
  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog
      .filter(f => !q || f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
        || (f.group || "").toLowerCase().includes(q));
  }, [catalog, query]);

  const byOrigin = useMemo(() => {
    const out = {};
    for (const f of available) (out[f.origin] = out[f.origin] || []).push(f);
    return out;
  }, [available]);

  const atCap = chosen.size >= max;

  const toggle = (key) => {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key);
    else if (next.size < max) next.add(key);
    else return;                       // silently refusing would look broken
    onChange?.([...next]);
  };

  return (
    <div
      style={{
        position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 60,
        width: 380, maxHeight: 520, overflowY: "auto",
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
        boxShadow: "0 12px 32px rgba(15,23,42,0.16)", padding: 14,
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontSize: 13.5 }}>Fields to display</strong>
        <span style={{
          fontSize: 12, fontWeight: 800,
          color: atCap ? "#b45309" : "#6b7280",
          fontVariantNumeric: "tabular-nums",
        }}>
          {chosen.size} / {max}
        </span>
      </div>

      <p style={{ margin: "6px 0 10px", fontSize: 11.5, color: "#6b7280", lineHeight: 1.45 }}>
        Applies to what is shown. Filtering, sorting and export are not limited to these.
      </p>

      <input
        id={`field-picker-search-${view}`}
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search fields…"
        style={{
          width: "100%", padding: "7px 10px", borderRadius: 8,
          border: "1px solid #d1d5db", fontSize: 13, marginBottom: 10,
        }}
      />

      {ORIGIN_ORDER.filter(o => (byOrigin[o] || []).length).map(origin => {
        const meta = ORIGIN_META[origin];
        return (
          <div key={origin} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginBottom: 5 }}>
              <span style={{
                width: 7, height: 7, borderRadius: 999, background: meta.color, flexShrink: 0,
              }} />
              <span style={{ fontSize: 11.5, fontWeight: 800, color: meta.color }}>
                {meta.label}
              </span>
              <span style={{ fontSize: 10.5, color: "#9ca3af" }}>{meta.hint}</span>
            </div>
            <div style={{ display: "grid", gap: 1 }}>
              {byOrigin[origin].map(f => {
                const on = chosen.has(f.key);
                const blocked = !on && atCap;
                return (
                  <label
                    key={f.key}
                    title={f.note || f.key}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 8,
                      padding: "4px 6px", borderRadius: 6, fontSize: 13,
                      cursor: blocked ? "not-allowed" : "pointer",
                      opacity: blocked ? 0.45 : 1,
                      background: on ? "#eef2ff" : "transparent",
                    }}
                  >
                    <input
                      id={`fp-${view}-${f.key}`}
                      type="checkbox"
                      checked={on}
                      disabled={blocked}
                      onChange={() => toggle(f.key)}
                      style={{ marginTop: 2, accentColor: "#6366f1", cursor: "inherit" }}
                    />
                    <span style={{ lineHeight: 1.35 }}>
                      {f.label}
                      <span style={{ color: "#9ca3af", fontSize: 11, display: "block" }}>
                        {f.group}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      {available.length === 0 && (
        <div style={{ fontSize: 12.5, color: "#9ca3af", padding: "10px 2px" }}>
          No fields match “{query}”.
        </div>
      )}

      <div style={{
        display: "flex", gap: 8, justifyContent: "space-between",
        borderTop: "1px solid #f3f4f6", paddingTop: 10, marginTop: 4,
      }}>
        <button
          className="btn-secondary"
          onClick={() => onChange?.(defaults.slice(0, max))}
          style={{ padding: "6px 11px", borderRadius: 8, fontSize: 12 }}
        >
          Reset
        </button>
        <button
          className="btn-primary"
          onClick={() => onClose?.()}
          style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12 }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

/** Render one field's value for a table cell or a card line. */
export function formatFieldValue(value, kind) {
  if (value === null || value === undefined || value === "") return "";
  if (kind === "bool") return value ? "Yes" : "No";
  if (kind === "int" || kind === "float") return Number(value).toLocaleString();
  if (kind === "date") {
    const d = new Date(value);
    return isNaN(d) ? String(value) : d.toLocaleDateString();
  }
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Read a field off an ad from the list API.
 *
 * Mirrors the server's resolver: creative-level fields come from the first
 * creative, AI fields are a dotted path into the stored read.
 */
export function readField(ad, field) {
  if (!ad || !field) return null;
  // The detail endpoint resolves every catalogue field on the server, the same
  // way the CSV export does. Prefer that over re-deriving it here.
  if (ad.fields && Object.prototype.hasOwnProperty.call(ad.fields, field.key)) {
    return ad.fields[field.key];
  }
  const creative = (ad.creatives || [])[0] || null;

  if (field.key.startsWith("ai.")) {
    const path = field.key.slice(3).split(".");
    let cur = creative?.visual || null;
    // The list response carries a compact summary; fall back to its flat keys.
    if (cur && path.length && cur[path[path.length - 1]] !== undefined) {
      return cur[path[path.length - 1]];
    }
    cur = creative?.visual_attributes_json || null;
    for (const part of path) {
      if (!cur || typeof cur !== "object") return null;
      cur = cur[part];
    }
    return cur;
  }

  if (field.key === "snapshot_state") {
    return (ad.creatives || []).some(c => c.snapshot_cached_at);
  }
  if (field.entity === "creative" || (creative && creative[field.key] !== undefined)) {
    return creative ? creative[field.key] : null;
  }
  return ad[field.key];
}
